import { Component, Input, AfterViewInit, ViewChild, ElementRef, ChangeDetectorRef, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Lightbox } from 'ngx-lightbox';

// An art-directed alternative, offered ahead of src/webpSrc.  The first entry
// whose media query matches wins, so list the narrowest breakpoint first.
export interface IllustrationSource {
  media:string;
  src:string;
  webpSrc?:string;
}

// NgOptimizedImage is available in this app, but is deliberately not used here:
// it does not support <picture>/<source>, which is how the webp fallback works.
@Component({
  selector: 'app-illustration',
  templateUrl: './illustration.component.html',
  styleUrls: ['./illustration.component.scss']
})
export class IllustrationComponent implements AfterViewInit{

  @ViewChild('image', { static: false, read: ElementRef })
  imageElement?:ElementRef<HTMLImageElement>;

  @Input()
  src?:string;

  @Input()
  webpSrc?:string;

  // Optional per-breakpoint alternatives.  Only use these when the artwork
  // really differs by breakpoint - the aspect ratio still comes from
  // width/height, so every entry must share the ratio of src.
  @Input()
  sources?:IllustrationSource[];

  // Decorative by default.  Supply real text for meaningful screenshots.
  @Input()
  alt:string = '';

  @Input()
  clickable:boolean = true;

  // Kept under the old name so no call site has to change - maps to object-position.
  @Input()
  backgroundPosition:string = 'center';

  // Kept under the old name so no call site has to change - painted as an overlay layer.
  @Input()
  backgroundImageOverlayCss?:string;

  // These arrive as strings from templates (width="1586"), so coerce before use.
  @Input()
  width:number;

  @Input()
  height:number;

  @Input()
  cover:boolean = false;

  // For the image most likely to be the page's Largest Contentful Paint, such as a blog
  // post's hero: fetched straight away at high priority rather than lazily, and shown as
  // soon as it arrives rather than faded in, since LCP only counts it once it is visible.
  @Input()
  priority:boolean = false;

  // Only ever set in the browser, so a server-rendered illustration is visible
  // immediately and the fade is a pure enhancement.
  pending = false;

  constructor(private _lightbox: Lightbox,
              private _changeDetector: ChangeDetectorRef,
              @Inject(PLATFORM_ID) private _platformId: Object) {
  }

  ngAfterViewInit() {
    if(!isPlatformBrowser(this._platformId)) {
      return;
    }
    // A prerendered or cached image can already be complete before the (load)
    // listener was attached, in which case (load) never fires.  Only fade in the
    // images that are actually still on their way, and never a priority image.
    let image = this.imageElement?.nativeElement;
    if(image && !image.complete && !this.priority) {
      this.pending = true;
      this._changeDetector.detectChanges();
    }
  }

  onImageLoad = () => {
    this.pending = false;
  }

  // Never leave a broken image permanently invisible.
  onImageError = () => {
    this.pending = false;
  }

  get hasIntrinsicRatio():boolean {
    return !!(this.width && this.height);
  }

  get paddingBottom():string {
    return this.hasIntrinsicRatio ? 100*(+this.height / +this.width)+'%' : '0';
  }

  get explicitHeight():string|null {
    return this.hasIntrinsicRatio ? null : '100%';
  }

  openLightboxImage = (event) => {
    if(!this.clickable) {
      return;
    }
    // currentSrc is whatever the browser actually picked out of the <picture>,
    // so the lightbox never gets handed a webp the browser cannot decode.
    let image = this.imageElement?.nativeElement;
    let src = (image && image.currentSrc) || this.webpSrc || this.src;
    if(src) {
      this._lightbox.open([{
        src: src,
        thumb: null,
        caption: this.alt || null
      }], 0);
    }
  }

}
